import useLocalStorage from 'hooks/useLocalStorageState'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'

import { ReactComponent as DatepickerIcon } from './calendar-icon.svg'
import { ReactComponent as Chevron } from './chevron.svg'
import styles from './SmartDatepicker.module.scss'

const startOfTheWeek = moment().startOf('week')
const startOfMonth = moment().startOf('month')

export const useGetSavedSmartDatepickerDates = localstorageName => {
  const relDate = localStorage.getItem(`${localstorageName}-sdp-relative`)
  const startDate = localStorage.getItem(`${localstorageName}-sdp-start`)
  const endDate = localStorage.getItem(`${localstorageName}-sdp-end`)

  if (relDate) {
    return getRealDateFromRelDate(relDate)
  }

  if (startDate && endDate) {
    return [moment(startDate), moment(endDate)]
  }

  return null
}

export const smartDateTypes = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this week',
  last7days: 'last 7 days',
  last14days: 'last 14 days',
  last30days: 'last 30 days',
  minus0Month: '-0mo',
  minus1Month: '-1mo',
  minus2Month: '-2mo',
  minus3Month: '-3mo',
  minus4Month: '-4mo',
  minus5Month: '-5mo',
  minus6Month: '-6mo',
  minus7Month: '-7mo',
  minus8Month: '-8mo',
  minus9Month: '-9mo',
  minus10Month: '-10mo',
  minus11Month: '-11mo',
  quarterMinus0: 'quarterMinus0',
  quarterMinus1: 'quarterMinus1',
  quarterMinus2: 'quarterMinus2',
  quarterMinus3: 'quarterMinus3',
  quarterMinus4: 'quarterMinus4'
}

const {
  today,
  yesterday,
  thisWeek,
  last7days,
  last14days,
  last30days,
  currentPlus1Month,
  currentPlus2Months,
  currentPlus3Months,
  yearToDate,
  minus0Month,
  minus1Month,
  minus2Month,
  minus3Month,
  minus4Month,
  minus5Month,
  minus6Month,
  minus7Month,
  minus8Month,
  minus9Month,
  minus10Month,
  minus11Month,
  quarterMinus0,
  quarterMinus1,
  quarterMinus2,
  quarterMinus3,
  quarterMinus4
} = smartDateTypes

const smartMonths = [
  minus0Month,
  minus1Month,
  minus2Month,
  minus3Month,
  minus4Month,
  minus5Month,
  minus6Month,
  minus7Month,
  minus8Month,
  minus9Month,
  minus10Month,
  minus11Month
]

const smartQuarters = [
  quarterMinus0,
  quarterMinus1,
  quarterMinus2,
  quarterMinus3,
  quarterMinus4
]

export const getRealDateFromRelDate = relativeDate => {
  switch (relativeDate) {
    case today:
      return [moment(), moment()]

    case yesterday:
      return [moment().subtract(1, 'days'), moment().subtract(1, 'days')]

    case thisWeek:
      return [moment(startOfTheWeek), moment()]

    case last7days:
      return [moment().subtract(6, 'days'), moment()]

    case last14days:
      return [moment().subtract(13, 'days'), moment()]

    case last30days:
      return [moment().subtract(29, 'days'), moment()]

    case currentPlus1Month:
      return [moment().subtract(1, 'month').startOf('month'), moment()]

    case currentPlus2Months:
      return [moment().subtract(2, 'month').startOf('month'), moment()]

    case currentPlus3Months:
      return [moment().subtract(3, 'month').startOf('month'), moment()]

    case minus0Month:
      return [moment(startOfMonth), moment()]

    case minus1Month:
      return [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month')
      ]

    case minus2Month:
      return [
        moment().subtract(2, 'month').startOf('month'),
        moment().subtract(2, 'month').endOf('month')
      ]
    case minus3Month:
      return [
        moment().subtract(3, 'month').startOf('month'),
        moment().subtract(3, 'month').endOf('month')
      ]

    case minus4Month:
      return [
        moment().subtract(4, 'month').startOf('month'),
        moment().subtract(4, 'month').endOf('month')
      ]
    case minus5Month:
      return [
        moment().subtract(5, 'month').startOf('month'),
        moment().subtract(5, 'month').endOf('month')
      ]
    case minus6Month:
      return [
        moment().subtract(6, 'month').startOf('month'),
        moment().subtract(6, 'month').endOf('month')
      ]
    case minus7Month:
      return [
        moment().subtract(7, 'month').startOf('month'),
        moment().subtract(7, 'month').endOf('month')
      ]
    case minus8Month:
      return [
        moment().subtract(8, 'month').startOf('month'),
        moment().subtract(8, 'month').endOf('month')
      ]
    case minus9Month:
      return [
        moment().subtract(9, 'month').startOf('month'),
        moment().subtract(9, 'month').endOf('month')
      ]
    case minus10Month:
      return [
        moment().subtract(10, 'month').startOf('month'),
        moment().subtract(10, 'month').endOf('month')
      ]
    case minus11Month:
      return [
        moment().subtract(11, 'month').startOf('month'),
        moment().subtract(11, 'month').endOf('month')
      ]

    case quarterMinus0:
      return [moment().startOf('quarter'), moment().endOf('quarter')]

    case quarterMinus1:
      return [
        moment().subtract(1, 'Q').startOf('quarter'),
        moment().subtract(1, 'Q').endOf('quarter')
      ]

    case quarterMinus2:
      return [
        moment().subtract(2, 'Q').startOf('quarter'),
        moment().subtract(2, 'Q').endOf('quarter')
      ]

    case quarterMinus3:
      return [
        moment().subtract(3, 'Q').startOf('quarter'),
        moment().subtract(3, 'Q').endOf('quarter')
      ]

    case quarterMinus4:
      return [
        moment().subtract(4, 'Q').startOf('quarter'),
        moment().subtract(4, 'Q').endOf('quarter')
      ]

    case yearToDate:
      return [moment().startOf('year'), moment()]

    default:
      return [moment().subtract(7, 'days'), moment()]
  }
}

const SmartDatepicker = ({
  onChangeStartDate,
  onChangeEndDate,
  className,
  inputClassName,
  startDate,
  endDate,
  isAlignedRight,
  minDate,
  maxDate,
  showDatepickerIcon = true,
  localstorageName,
  hasSmartOptions = true,
  smartDates = [
    today,
    yesterday,
    thisWeek,
    last7days,
    last14days,
    last30days,
    minus0Month
  ],
  showMonths = false,
  showQuarters = false,
  extraSmartOptions = null,
  textOverride = null,
  dismissCurrentDay,
  appendDropdownToBody = false
}) => {
  const smartDatesToShow = [
    ...smartDates,
    ...(showMonths ? smartMonths : []),
    ...(showQuarters ? smartQuarters : [])
  ]

  const [isOpen, setOpen] = useState(false)

  const handleInputClick = () => {
    setOpen(!isOpen)
  }

  function calculateMaxDate(maxDate, dismissCurrentDay) {
    if (maxDate) {
      return maxDate.toDate()
    } else if (dismissCurrentDay) {
      return moment().subtract(1, 'day').toDate()
    } else {
      return Date.now()
    }
  }

  const handleSetStart = startDate => {
    onChangeStartDate(moment(startDate))

    if (moment(endDate).isBefore(moment(startDate))) {
      handleSetEnd(moment(startDate).add(1, 'days').toDate())
    }

    if (localstorageName) {
      localStorage.setItem(`${localstorageName}-sdp-start`, moment(startDate).format())
      localStorage.removeItem(`${localstorageName}-sdp-relative`)

      if (!localStorage.getItem(`${localstorageName}-sdp-end`)) {
        localStorage.setItem(`${localstorageName}-sdp-end`, moment(endDate).format())
      }
    }
  }

  const handleSetEnd = endDate => {
    onChangeEndDate(moment(endDate))
    if (localstorageName) {
      localStorage.setItem(`${localstorageName}-sdp-end`, moment(endDate).format())
      localStorage.removeItem(`${localstorageName}-sdp-relative`)

      if (!localStorage.getItem(`${localstorageName}-sdp-start`)) {
        localStorage.setItem(`${localstorageName}-sdp-start`, moment(startDate).format())
      }
    }
  }

  const setRelativeDate = relativeDate => {
    const [startDate, endDate] = getRealDateFromRelDate(relativeDate)

    if (localstorageName) {
      localStorage.removeItem(`${localstorageName}-sdp-start`)
      localStorage.removeItem(`${localstorageName}-sdp-end`)
      localStorage.setItem(`${localstorageName}-sdp-relative`, relativeDate)
    }

    onChangeStartDate(startDate)
    onChangeEndDate(endDate)
  }

  const savedSmartDatepickerDates = useGetSavedSmartDatepickerDates(localstorageName)

  useEffect(() => {
    if (localstorageName && savedSmartDatepickerDates) {
      const [startDate, endDate] = savedSmartDatepickerDates

      onChangeStartDate(startDate)
      onChangeEndDate(endDate)
    }
  }, []) // eslint-disable-line

  const node = useRef(null)
  const dropdownPortalRef = useRef(null)
  const [portalPosition, setPortalPosition] = useState({ left: 0, top: 0 })

  const handleClickOutside = e => {
    if (node.current?.contains(e.target)) {
      return
    }
    if (appendDropdownToBody && dropdownPortalRef.current?.contains(e.target)) {
      return
    }
    setOpen(false)
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  let selectedDateText = ''

  if (
    moment(startDate).isSame(moment(endDate), 'day') &&
    moment(startDate).isSame(moment(), 'day')
  ) {
    selectedDateText = `Today`
  } else if (
    moment(startDate).isSame(moment(endDate), 'day') &&
    moment(startDate).isSame(moment().subtract('1', 'day'), 'day')
  ) {
    selectedDateText = `Yesterday`
  } else if (
    moment(startDate).isSame(startOfTheWeek, 'day') &&
    moment(endDate).isSame(moment(), 'day')
  ) {
    selectedDateText = 'This week'
  } else if (
    moment(startDate).isSame(startOfMonth, 'day') &&
    moment(endDate).isSame(moment(), 'day')
  ) {
    selectedDateText = 'This month'
  } else if (
    moment(startDate).isSame(moment().subtract(1, 'months').startOf('month'), 'day') &&
    moment(endDate).isSame(moment().subtract(1, 'months').endOf('month'), 'day')
  ) {
    selectedDateText = 'Last month'
  } else if (
    moment(endDate).isSame(moment(), 'day') &&
    moment().diff(startDate, 'days') < 31
  ) {
    selectedDateText = `Last ${moment().diff(startDate, 'days') + 1} days`
  } else if (moment(endDate).isSame(startDate)) {
    selectedDateText =
      moment(startDate).format('MMMM D') +
      (moment(startDate).isSame(moment(), 'year')
        ? ''
        : ` ${moment(startDate).format('YYYY')}`)
  } else {
    selectedDateText =
      moment(startDate).format('MMM D') +
      (moment(startDate).isSame(moment(), 'year')
        ? ''
        : ` ${moment(startDate).format('YYYY')}`) +
      ' - ' +
      moment(endDate).format('MMM D') +
      (moment(endDate).isSame(moment(), 'year')
        ? ''
        : ` ${moment(endDate).format('YYYY')}`)
  }

  const [showMoreOptions, setShowMoreOptions] = useLocalStorage(
    'prefersMoreDatepickerOptions',
    false
  )

  const distanceTreshold = 60

  const distanceFromLeft = node.current?.getBoundingClientRect()?.left
  const distanceFromRight =
    window.innerWidth - distanceFromLeft - node?.current?.offsetWidth

  const dropdownWidth = 236

  useLayoutEffect(() => {
    if (!isOpen || !appendDropdownToBody || !node.current) {
      return undefined
    }

    function updatePortalPosition() {
      const el = node.current
      if (!el) {
        return
      }
      const rect = el.getBoundingClientRect()
      const dfl = rect.left
      const dfr = window.innerWidth - rect.left - rect.width
      let left
      if (dfl < distanceTreshold) {
        left = rect.left
      } else if (dfr < distanceTreshold) {
        left = rect.right - dropdownWidth
      } else if (isAlignedRight) {
        left = rect.right - dropdownWidth
      } else {
        left = rect.left
      }
      const top = rect.bottom + 4
      const maxLeft = window.innerWidth - dropdownWidth - 8
      left = Math.min(Math.max(8, left), maxLeft)
      setPortalPosition({ left, top })
    }

    updatePortalPosition()
    window.addEventListener('scroll', updatePortalPosition, true)
    window.addEventListener('resize', updatePortalPosition)
    return () => {
      window.removeEventListener('scroll', updatePortalPosition, true)
      window.removeEventListener('resize', updatePortalPosition)
    }
  }, [isOpen, appendDropdownToBody, isAlignedRight, startDate, endDate])

  function getUIdateName(dateName) {
    if (dateName.match(/-(\d+)+mo$/)) {
      const diff = dateName.match(/-(\d+)mo$/)[1]

      return moment().subtract(diff, 'month').startOf('month').format('MMM YYYY')
    }

    if (dateName.startsWith('quarter')) {
      const index = dateName.match(/quarterMinus(\d)/)[1]
      return moment().subtract(index, 'Q').format('[q]Q-Y')
    }

    return dateName
  }

  const dropdownBody = (
    <>
      <div className={styles.datepickers}>
        <div className={styles.singleDatepicker}>
          <div className={styles.datepickerLabel}>From</div>
          <DatePicker
            selected={startDate.toDate()}
            selectsStart
            startDate={startDate.toDate()}
            endDate={endDate.toDate()}
            onChange={handleSetStart}
            minDate={minDate && minDate.toDate()}
            maxDate={calculateMaxDate(maxDate, dismissCurrentDay)}
          />
        </div>
        <div className={styles.singleDatepicker}>
          <div className={styles.datepickerLabel}>To</div>
          <DatePicker
            selected={endDate.toDate()}
            selectsEnd
            startDate={startDate.toDate()}
            endDate={endDate.toDate()}
            onChange={handleSetEnd}
            minDate={startDate && startDate.toDate()}
            maxDate={maxDate ? maxDate.toDate() : Date.now()}
          />
        </div>
      </div>
      {hasSmartOptions && (
        <div className={styles.bottom}>
          {extraSmartOptions}
          {smartDatesToShow.slice(0, 4).map(dateName => (
            <button key={dateName} onClick={() => setRelativeDate(dateName)}>
              {getUIdateName(dateName)}
            </button>
          ))}
          {!showMoreOptions && smartDatesToShow.length > 4 && (
            <button
              className={styles.moreLessOptions}
              onClick={() => setShowMoreOptions(true)}
            >
              More options
            </button>
          )}
          {showMoreOptions && smartDatesToShow.length > 4 && (
            <div className={styles.seperator} />
          )}{' '}
          {showMoreOptions && smartDatesToShow.length > 4 && (
            <>
              {smartDatesToShow
                .slice(4, smartDatesToShow.length - 5)
                .map(dateName => (
                  <button key={dateName} onClick={() => setRelativeDate(dateName)}>
                    {getUIdateName(dateName)}
                  </button>
                ))}
            </>
          )}
          {showMoreOptions && smartDatesToShow.length > 5 && (
            <div className={styles.seperator} />
          )}
          {showMoreOptions && smartDatesToShow.length > 5 && (
            <>
              {smartDatesToShow
                .slice(smartDatesToShow.length - 5, smartDatesToShow.length)
                .map(dateName => (
                  <button key={dateName} onClick={() => setRelativeDate(dateName)}>
                    {getUIdateName(dateName)}
                  </button>
                ))}
            </>
          )}
          {showMoreOptions && smartDatesToShow.length > 4 && (
            <button
              className={styles.moreLessOptions}
              onClick={() => setShowMoreOptions(false)}
            >
              Less options
            </button>
          )}
        </div>
      )}
    </>
  )

  return (
    <div
      className={[
        className,
        styles.smartDatepicker,
        isAlignedRight ? styles.right : ''
      ].join(' ')}
      ref={node}
    >
      <div
        className={inputClassName ? inputClassName : styles.visibleInput}
        onClick={handleInputClick}
      >
        {showDatepickerIcon && <DatepickerIcon className={styles.datepickerIcon} />}
        {textOverride || selectedDateText}
        <Chevron className={styles.chevron} />
      </div>

      {isOpen && !appendDropdownToBody && (
        <div
          className={styles.inner}
          style={{
            left: distanceFromLeft < distanceTreshold ? 0 : 'auto',
            right: distanceFromRight < distanceTreshold ? 0 : 'auto'
          }}
        >
          {dropdownBody}
        </div>
      )}

      {isOpen &&
        appendDropdownToBody &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownPortalRef}
            className={[styles.inner, styles.innerPortalDropdown].join(' ')}
            style={{
              left: portalPosition.left,
              top: portalPosition.top
            }}
          >
            {dropdownBody}
          </div>,
          document.body
        )}
    </div>
  )
}

SmartDatepicker.defaultProps = {
  minDate: null,
  maxDate: null
}

SmartDatepicker.propTypes = {
  handleChangeStart: PropTypes.func,
  handleChangeEnd: PropTypes.func,
  isRange: PropTypes.bool,
  appendDropdownToBody: PropTypes.bool
}

export default SmartDatepicker
